import { createFolderForProvider } from '../services/createFolderForProvider';
import { CreateNewFolderError } from '../types/CreateNewFolderError';
import { datapoints } from '../datapoints';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { FolderSelectorError } from '../store/schema/FolderSelectorError';
import { getAllFolderModels } from '../selectors/folderModelSelectors';
import { getCurrentLoadedFolderId } from '../selectors/selectedFolderSelectors';
import { getSelectedProvider } from '../selectors/selectedProviderSelectors';
import { HTTPResponseError } from '../types/HTTPResponseError';
import { HttpStatusCode } from 'owa-http-status-codes';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { orchestrator } from 'satcheljs';
import { trace } from 'owa-trace';
import {
    createdNewFolder,
    createNewFolder,
    creatingNewFolder,
    creatingNewFolderFailed,
    loadFolders,
} from '../actions/internalActions';

/**
 * SHOULD NOT BE USED DIRECTLY. EXPORTED ONLY FOR TESTING
 */
export const createNewFolderOrchestrator = orchestrator(createNewFolder, async actionMessage => {
    const { folderName } = actionMessage;

    if (!folderName || isStringNullOrWhiteSpace(folderName)) {
        // The folder name provided is not valid so we ignore it
        return;
    }

    const {
        FPFolderSelectorCreateNewFolder: {
            datapoint: { name: datapointName, customData: datapointCustomData },
        },
    } = datapoints;

    // Check locally if the folder name conflicts with current folder
    // if it does throw approriate error action
    const folderModels = getAllFolderModels();
    const conflictsWithExistingFolderName = folderModels.some(folder => folder.name === folderName);
    if (conflictsWithExistingFolderName) {
        creatingNewFolderFailed(FolderSelectorError.FolderAlreadyExists);

        // Log the datapoint
        const datapoint = new PerformanceDatapoint(datapointName);
        datapoint.addCustomData(datapointCustomData(CreateNewFolderError.ConflictFolderName));
        datapoint.endWithError(DatapointStatus.UserError);

        return;
    }

    const selectedProvider = getSelectedProvider();
    if (selectedProvider) {
        const datapoint = new PerformanceDatapoint(datapointName);
        let creationError:
            | { errorType: CreateNewFolderError.None }
            | { errorType: Exclude<CreateNewFolderError, 'None'>; error: Error } = {
            errorType: CreateNewFolderError.None,
        };

        try {
            creatingNewFolder(folderName);

            const parentFolderId = getCurrentLoadedFolderId();
            const folder = await createFolderForProvider(
                selectedProvider.type,
                selectedProvider.model.EndPointUrl,
                folderName,
                parentFolderId
            );

            createdNewFolder(folder);
        } catch (error) {
            trace.error(`Error while creating folder: ${error.toString()}`);

            const httpResponseError = error as HTTPResponseError;

            if (
                httpResponseError.response &&
                httpResponseError.response.status === HttpStatusCode.Conflict
            ) {
                creatingNewFolderFailed(FolderSelectorError.FolderAlreadyExists);
                creationError = { errorType: CreateNewFolderError.ConflictFolderName, error };
            } else {
                creatingNewFolderFailed(FolderSelectorError.CreatingNewFolder);
                creationError = { errorType: CreateNewFolderError.Generic, error };
            }
        } finally {
            // Log the datapoint accordingly based on if the folder was created successfully or not
            datapoint.addCustomData(datapointCustomData(creationError.errorType));

            if (creationError.errorType !== CreateNewFolderError.None) {
                datapoint.endWithError(DatapointStatus.ServerError, creationError.error);
            } else {
                datapoint.end();
            }
        }
    }
});

orchestrator(createdNewFolder, actionMessage => {
    // Open the newly created folder
    const { folder } = actionMessage;
    loadFolders(folder.id);
});
